import { deviceToken } from './api.ts';
/**
 * Live connection to the server.
 *
 * Reconnects with backoff, because a shop-floor PC on retail broadband
 * will drop repeatedly and a tight retry loop turns one flaky router
 * into a self-inflicted denial of service across 120 devices.
 */
export type LiveState = 'connecting' | 'open' | 'offline';

export interface LiveEvent {
  // 'chat' has been published by the server since chat was
  // built; it was simply never listed here, so no component
  // could listen for it.
  readonly type: 'ready' | 'ping' | 'message' | 'chat'
    | 'request' | 'force_logout';
  readonly sessionId?: string;
  readonly siteId?: string;
  readonly categoryId?: string;
  readonly seq?: string;
}

interface Options {
  readonly siteId?: string;
  readonly token?: string;
  readonly onEvent: (event: LiveEvent) => void;
  readonly onState: (state: LiveState) => void;
}

const MAX_BACKOFF_MS = 30_000;

export const connectLive = (options: Options): (() => void) => {
  let socket: WebSocket | null = null;
  let attempt = 0;
  let timer: number | undefined;
  let closed = false;

  const url = (): string => {
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const params = new URLSearchParams();
    // Read the token at connect time, not when the effect first ran.
    // The shell rotates it every 15 minutes, so a socket reconnecting
    // with the string it captured at start-up is rejected.
    const live = options.token ? deviceToken() ?? options.token : null;
    if (live) params.set('token', live);
    if (options.siteId) params.set('siteId', options.siteId);
    return `${scheme}://${window.location.host}/live?${params}`;
  };

  const open = () => {
    if (closed) return;
    options.onState(attempt === 0 ? 'connecting' : 'offline');

    socket = new WebSocket(url());

    socket.onopen = () => {
      attempt = 0;
      options.onState('open');
    };

    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as LiveEvent;
        // Heartbeats keep the proxy happy; nothing above cares.
        if (parsed.type !== 'ping') options.onEvent(parsed);
      } catch {
        // A frame we cannot parse is not worth tearing the socket down.
      }
    };

    socket.onclose = (event) => {
      if (closed) return;
      options.onState('offline');

      // 4401 is almost always a token that rotated while the socket
      // was down, not a revoked device. Giving up entirely left a
      // counter on Offline until somebody restarted the app, while
      // its HTTP polling kept refreshing last_seen_at -- so head
      // office saw a device that looked perfectly healthy. Retry,
      // but from a long backoff: once a minute, not in a loop.
      if (event.code === 4401) attempt = Math.max(attempt, 5);

      attempt += 1;
      const wait = Math.min(1000 * 2 ** (attempt - 1), MAX_BACKOFF_MS);
      // Jitter so 120 devices reconnecting after one outage do not
      // arrive in lockstep.
      const jittered = wait * (0.7 + Math.random() * 0.6);
      timer = window.setTimeout(open, jittered);
    };

    socket.onerror = () => socket?.close();
  };

  // A tab woken from background may hold a socket the OS already
  // killed. Reconnecting on visibility avoids a counter screen that
  // looks connected but silently stopped receiving hours ago.
  const onVisible = () => {
    if (
      document.visibilityState === 'visible' &&
      socket?.readyState !== WebSocket.OPEN
    ) {
      window.clearTimeout(timer);
      attempt = 0;
      open();
    }
  };

  document.addEventListener('visibilitychange', onVisible);
  open();

  return () => {
    closed = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
    socket?.close();
  };
};

/**
 * A place for components to hear about live events.
 *
 * The socket is opened once, by the board. Components deeper in the
 * tree need the same events without opening a second connection, so
 * the board hands them here and anyone interested subscribes.
 */
type Listener = (e: LiveEvent) => void;
const listeners = new Set<Listener>();

export const onLive = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const emitLive = (e: LiveEvent): void => {
  for (const fn of listeners) fn(e);
};
